import { createSelector } from '@reduxjs/toolkit';

import type { StateWithSidekickChat } from './chatTypes';
import type { ConversationBranchSession } from './conversation-branch-types';

/** Navigation state rendered beside an edited user query. */
export type ConversationBranchNavigation = {
  current: number;
  total: number;
  previousConversationId: string | null;
  nextConversationId: string | null;
};

/** Selects the session-only conversation lineage registry. */
export const selectConversationBranchSession = (
  state: StateWithSidekickChat
): ConversationBranchSession => state.aiSidekickChat.branchSession;

const selectActiveChatConversationId = (state: StateWithSidekickChat) =>
  state.aiSidekickChat.conversationId;

/** Selects branch position and adjacent conversation identifiers. */
export const selectConversationBranchNavigation = createSelector(
  [selectConversationBranchSession, selectActiveChatConversationId],
  (branchSession, conversationId): ConversationBranchNavigation | null => {
    const { activeConversationId, conversationOrder } = branchSession;
    if (
      activeConversationId === null ||
      activeConversationId !== conversationId ||
      conversationOrder.length < 2
    ) {
      return null;
    }

    const activeIndex = conversationOrder.indexOf(activeConversationId);
    if (activeIndex === -1) {
      return null;
    }

    return {
      current: activeIndex + 1,
      total: conversationOrder.length,
      previousConversationId:
        activeIndex > 0 ? conversationOrder[activeIndex - 1] : null,
      nextConversationId:
        activeIndex < conversationOrder.length - 1
          ? conversationOrder[activeIndex + 1]
          : null,
    };
  }
);
